/**
 * A filter bar that collapses to a compact row on a short or narrow screen.
 *
 * The low-res pass (DP-11): at 1366×768 the catalog's five-select filter
 * panel alone measured ~150px tall, which on a *short* viewport is most of
 * what little height the grid below it had to work with — the operator's
 * screenshot showed six visible rows for exactly this reason. Below the
 * threshold this renders `search` plus a toggle; the rest (`children`) only
 * mounts when expanded. Above it, everything always shows, same as before
 * this existed.
 *
 * The threshold (`COMPACT_QUERY`, `theme.ts`) is **height ≤ 800 or width <
 * 1200** — an OR, not Mantine's usual single named breakpoint, because a
 * short desktop window (1366×768) needs the same compacting a narrow phone
 * does, and neither axis alone would catch it. It is shared with `DataGrid`
 * (dense row spacing) and the pages that trim their own surrounding chrome
 * alongside both — see its own doc comment for the full list.
 *
 * The expand/collapse choice persists per screen (`storageKey`) in
 * localStorage, per the brief; *whether* the panel is compact at all is
 * pure media query and never persisted — that is a fact about the viewport,
 * not a preference.
 */
import type { ReactNode } from 'react'
import { Button, Group, Paper } from '@mantine/core'
import { useLocalStorage, useMediaQuery } from '@mantine/hooks'

import { COMPACT_QUERY } from '../theme'

export { COMPACT_QUERY }

interface Props {
  /** Scopes the remembered open/closed state to one screen, e.g. `'catalog'`. */
  storageKey: string
  /** Always visible, even collapsed — typically a search `TextInput`. */
  search: ReactNode
  /** The selects etc. that collapse away on a short/narrow screen. */
  children: ReactNode
  /** Rendered after `children`, e.g. a "clear filters" icon — always visible,
   * since a single icon button costs little even in the compact row. */
  extra?: ReactNode
}

export function FilterPanel({ storageKey, search, children, extra }: Props) {
  const compact = useMediaQuery(COMPACT_QUERY)
  // `false`, unconditionally — not "collapsed on phone, open on desktop".
  // `open` is only ever *read* through `showFilters` below, which already
  // short-circuits to `true` the instant `compact` is false, so this default
  // is only ever observed on a screen short/narrow enough that the brief
  // wants it compact in the first place. Defaulting it `true` instead once
  // measured 8 visible catalog rows at 1366x768 — the toggle existed and
  // worked, but a first-ever visit never got to benefit from it.
  const [open, setOpen] = useLocalStorage({
    key: `qtapp-filters-open-${storageKey}`,
    defaultValue: false,
  })
  const showFilters = !compact || open

  return (
    // `p={4}` compact, not the usual `"sm"` (~12px/side): this row's whole
    // reason to exist is giving the grid below back the height a 5-select
    // panel cost it, so the one row that always shows pays its own way too.
    <Paper withBorder p={compact ? 4 : 'sm'}>
      <Group align="flex-end" gap="sm" wrap="wrap">
        {search}
        {compact && (
          <Button
            variant="default"
            size="sm"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
          >
            {open ? 'הסתר מסננים' : 'מסננים'}
          </Button>
        )}
        {showFilters && children}
        {extra}
      </Group>
    </Paper>
  )
}
